import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import ChatPane, { ChatToolEvent } from '../components/capstone/ChatPane';
import CapstoneTerminal, { TerminalHandle } from '../components/capstone/CapstoneTerminal';
import CodeViewer from '../components/capstone/CodeViewer';
import FileTree from '../components/capstone/FileTree';
import PlanChecklist from '../components/capstone/PlanChecklist';
import ThemeToggle from '../components/ThemeToggle';
import DocumentationButton from '../components/Documentation/DocumentationButton';
import DocumentationModal from '../components/Documentation/DocumentationModal';
import { capstoneApi, capstonePreviewSrc } from '../services/capstoneApi';
import {
  ContainerStateName,
  ConversationMessage,
  FileEntry,
  HistorySnapshot,
  PlanStep,
  ProjectState,
  ProjectStatus,
} from '../types/capstone';

const STATUS_PILL: Record<ProjectStatus, { label: string; classes: string }> = {
  shaping:  { label: 'Shaping',  classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  building: { label: 'Building', classes: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  ready:    { label: 'Ready',    classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  paused:   { label: 'Paused',   classes: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  archived: { label: 'Archived', classes: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
  failed:   { label: 'Failed',   classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  deleted:  { label: 'Deleted',  classes: 'bg-red-200 text-red-800 dark:bg-red-950 dark:text-red-300' },
};

// Friendly, rotating status lines shown while the container provisions (the
// first build can pull a multi-GB image — keep the user reassured, not bored).
const BOOT_MESSAGES = [
  'Spinning up your private build environment…',
  'First run sets up the toolchain — this can take a couple of minutes…',
  'Getting your sandbox ready…',
  'Almost there — preparing to build…',
  'Hang tight, finalizing the environment…',
];

// Grace period before auto-waking a hibernated project. Long enough that
// click-in-click-out browsing of the project list never provisions an ACI
// (a stray wake bills for the whole idle window), short enough to be
// invisible next to the 15-25s provision time. The waking spinner shows
// IMMEDIATELY on open — this delay only gates the actual /resume call.
const WAKE_GRACE_MS = 5000;

const CapstoneProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Ultimate is the top plan — nothing to upgrade to, so hide the Upgrade CTA.
  const isUltimate = user?.subscription_tier === 'ultimate';
  // Set by CapstonePage when the user clicks "Start project →" from home.
  // We auto-send this as the first iteration message after the project state
  // loads, so the user doesn't have to re-type or re-press Send.
  const autoStartMessage = (location.state as { autoStartMessage?: string } | null)?.autoStartMessage;
  const [autoStartFired, setAutoStartFired] = useState(false);

  const [project, setProject] = useState<ProjectState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Conversation persisted from backend (loaded on mount). Streaming text for
  // the in-flight turn lives separately and is appended to the list when an
  // iteration_done event arrives.
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [streamingAssistant, setStreamingAssistant] = useState<string | null>(null);
  // Post-success learner recap — streams AFTER the turn's tool cards as its own
  // bubble ("What you built / Try it / Your files"); persisted server-side as a
  // separate assistant message.
  const [streamingRecap, setStreamingRecap] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState<PlanStep[] | null>(null);
  const [history, setHistory] = useState<HistorySnapshot | null>(null);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [containerState, setContainerState] = useState<ContainerStateName | null>(null);
  const [toolEvents, setToolEvents] = useState<ChatToolEvent[]>([]);
  // Most recent run_command string — drives the terminal's "↻ Re-run" button.
  const [lastCommand, setLastCommand] = useState<string | null>(null);
  // Browser-playable build (e.g. a WASM game). When set, the right pane offers
  // a "Play" tab with an <iframe> served via the backend preview proxy.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Preview reload nonce: the preview URL's capability token is deliberately
  // stable across container rebuilds, so `key={previewUrl}` alone never
  // re-mounts the iframe after a wake — it kept showing the cached "asleep"
  // page even with the server back up. Bumped on every container_status:ready.
  const [previewNonce, setPreviewNonce] = useState(0);
  // Output pane (pane 3) tab: build console vs running preview. The terminal
  // stays mounted (hidden) behind the preview so streamed output isn't lost.
  const [outputTab, setOutputTab] = useState<'terminal' | 'preview'>('terminal');
  // Rotating reassurance message during the (sometimes multi-minute) first
  // container provisioning, so the user doesn't think it's stuck.
  const [bootMsgIdx, setBootMsgIdx] = useState(0);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  // Elapsed seconds while an iteration is in flight — so a long build never
  // looks frozen even when the compiler is quiet.
  const [busySeconds, setBusySeconds] = useState(0);
  // Desktop breakpoint gate: md+ gets the drag-resizable 3-pane layout; below
  // it stays the single-column chat view (panes 2-3 were hidden there anyway).
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 768px)').matches
  );
  // While a pane divider is dragged the preview iframe must ignore pointer
  // events, or the drag dies the moment the cursor crosses the iframe.
  const [paneDragging, setPaneDragging] = useState(false);
  // Auto-wake: hibernated projects used to need a chat message to wake (bad —
  // it coupled "show me my project" with "do AI work"). Now the page calls
  // /resume itself after WAKE_GRACE_MS, or immediately on user intent.
  // wakeStartedRef guards double-fires; it also stands down when /iterate
  // takes over the wake (user sent a message first).
  const wakeStartedRef = useRef(false);
  // Wake-driven 'starting' shows "Waking…" copy instead of BOOT_MESSAGES.
  const [waking, setWaking] = useState(false);
  const [wakeFailed, setWakeFailed] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Right-pane code viewer state
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // Live build terminal (center pane). Imperative handle so streamed output
  // chunks write straight to xterm without re-rendering React per chunk.
  const terminalRef = useRef<TerminalHandle | null>(null);
  // Track which tool_call_ids are run_commands so we know to print an exit line
  // when their result arrives (write_file/read_file don't touch the terminal).
  const runCmdIds = useRef<Set<string>>(new Set());
  // Monotonic client-side turn id for grouping chat (backend iteration numbers
  // aren't reliable keys). Starts high so it never collides with the small
  // backend iterations on persisted history loaded from the server.
  const turnRef = useRef<number>(100000);

  // Load project state on mount.
  // Phase A: backend's GET /projects/{id} returns the row but with empty
  // conversation[]. We don't have a /messages endpoint yet, so the chat will
  // appear empty on initial load even if there are persisted turns. New
  // messages from /iterate stream in fine. This will be cleaned up when we
  // add a real conversation-fetch endpoint.
  useEffect(() => {
    if (!projectId) return;
    let active = true;
    capstoneApi.getProject(projectId)
      .then((p) => {
        if (!active) return;
        setProject(p);
        setMessages(p.conversation || []);
        setPlan(p.plan ?? null);
        setHistory(p.history_snapshot ?? null);
        setFiles(p.files || []);
        setContainerState(p.container?.state || null);
        if (p.container?.preview_url) {
          setPreviewUrl(p.container.preview_url);
          setOutputTab('preview');
        }
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err?.message || 'Failed to load project.');
      });
    return () => {
      active = false;
      abortRef.current?.abort();
    };
  }, [projectId]);

  const handleSend = (text: string) => {
    if (!projectId || busy) return;
    // A message wakes the container through /iterate anyway — stand down the
    // auto-wake timer so the two paths don't race (the backend's per-project
    // provision lock guards whatever overlap remains).
    wakeStartedRef.current = true;
    setBusy(true);
    setStreamingAssistant('');

    // Fold the previous turn's recap bubble into the message list (it's already
    // persisted server-side as its own assistant message) so it survives the
    // buffer reset below.
    if (streamingRecap) {
      const recapTurn = turnRef.current;
      const recapContent = streamingRecap;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: recapContent, iteration: recapTurn, ts: new Date().toISOString() },
      ]);
    }
    setStreamingRecap(null);

    // Stable client-side turn id: the user msg, this turn's tool cards, and the
    // assistant reply all share it, so they render together as one turn.
    turnRef.current += 1;
    const thisTurn = turnRef.current;

    // Optimistic user bubble while server is processing
    const userMessage: ConversationMessage = {
      role: 'user',
      content: text,
      iteration: thisTurn,
      ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    let assistantBuffer = '';
    let recapBuffer = '';

    const controller = new AbortController();
    abortRef.current = controller;

    capstoneApi.streamIterate(
      projectId,
      text,
      {
        onHistoryExtracted: (data) => {
          setHistory({
            extracted_at: new Date().toISOString(),
            concepts: data.concepts,
            languages: data.languages,
          });
        },
        onChatToken: (data) => {
          assistantBuffer += data.content;
          setStreamingAssistant(assistantBuffer);
        },
        onRecapToken: (data) => {
          recapBuffer += data.content;
          setStreamingRecap(recapBuffer);
        },
        onPlanUpdate: (data) => {
          setPlan(data.plan);
        },
        onContainerStatus: (data) => {
          setContainerState(data.state);
          if (data.state === 'ready') {
            setPreviewNonce((n) => n + 1);   // force the iframe to reload the revived app
          }
          if (data.preview_url) {
            setPreviewUrl(data.preview_url);
            setOutputTab('preview');   // auto-jump to the playable build
          }
        },
        onToolCallStart: (data) => {
          const cmd = data.tool === 'run_command'
            ? String((data.args as { command?: string })?.command ?? '')
            : undefined;
          setToolEvents((prev) => [
            ...prev,
            { id: data.tool_call_id, tool: data.tool, status: 'running', command: cmd, iteration: thisTurn },
          ]);
          // Echo the command into the live terminal as a "$ <cmd>" prompt line.
          if (cmd !== undefined) {
            runCmdIds.current.add(data.tool_call_id);
            setLastCommand(cmd);
            terminalRef.current?.writeCommand(cmd);
          }
        },
        onToolCallProgress: (data) => {
          // Stream each output chunk straight to xterm as it arrives.
          terminalRef.current?.write(data.chunk);
        },
        onToolCallResult: (data) => {
          setToolEvents((prev) =>
            prev.map((t) =>
              t.id === data.tool_call_id
                ? { ...t, status: data.success ? 'success' : 'failed', summary: data.summary }
                : t,
            ),
          );
          // Close out the command in the terminal with an exit line.
          if (runCmdIds.current.has(data.tool_call_id)) {
            terminalRef.current?.writeExit(data.exit_code, data.duration_seconds, data.timed_out);
          }
        },
        onFileTreeUpdate: (data) => {
          setFiles(data.files);
        },
        onStatusChange: (data) => {
          setProject((p) => (p ? { ...p, status: data.status } : p));
        },
        onIterationDone: (data) => {
          // Move streaming buffers into the persisted list. The recap is NOT
          // folded in here — it stays as the streamingRecap bubble (rendered
          // after this turn's tool cards) until the next turn starts, matching
          // where the server persisted it.
          if (assistantBuffer) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: assistantBuffer,
                iteration: thisTurn,
                ts: new Date().toISOString(),
              },
            ]);
          }
          setStreamingAssistant(null);
          setBusy(false);
          // Bump local iteration count
          setProject((p) => (p ? { ...p, iteration_count: data.iteration } : p));
        },
        onError: (data) => {
          assistantBuffer += `\n\n[error: ${data.message}]`;
          setStreamingAssistant(assistantBuffer);
          if (!data.recoverable) {
            setBusy(false);
          }
        },
      },
      controller.signal,
    ).catch(() => {
      // streamIterate already invokes onError for HTTP errors; this catch
      // handles abort + unexpected network failures so busy doesn't stay stuck.
      setBusy(false);
    });
  };

  // Re-run the most recent shell command directly against the container — no
  // LLM, no credits. Streams fresh output into the same terminal. Useful for
  // non-deterministic commands (timestamps, random data, network calls).
  const handleRerun = (command: string) => {
    if (!projectId || busy || !command) return;
    setBusy(true);

    // Group re-run cards with the latest turn.
    const rerunTurn = turnRef.current;

    const controller = new AbortController();
    abortRef.current = controller;

    capstoneApi.streamRunCommand(
      projectId,
      command,
      {
        onContainerStatus: (data) => {
          setContainerState(data.state);
          if (data.state === 'ready') {
            setPreviewNonce((n) => n + 1);   // force the iframe to reload the revived app
          }
          if (data.preview_url) {
            setPreviewUrl(data.preview_url);
            setOutputTab('preview');   // auto-jump to the playable build
          }
        },
        onToolCallStart: (data) => {
          const cmd = data.tool === 'run_command'
            ? String((data.args as { command?: string })?.command ?? '')
            : undefined;
          setToolEvents((prev) => [
            ...prev,
            { id: data.tool_call_id, tool: data.tool, status: 'running', command: cmd, iteration: rerunTurn },
          ]);
          if (cmd !== undefined) {
            runCmdIds.current.add(data.tool_call_id);
            setLastCommand(cmd);
            terminalRef.current?.writeCommand(cmd);
          }
        },
        onToolCallProgress: (data) => {
          terminalRef.current?.write(data.chunk);
        },
        onToolCallResult: (data) => {
          setToolEvents((prev) =>
            prev.map((t) =>
              t.id === data.tool_call_id
                ? { ...t, status: data.success ? 'success' : 'failed', summary: data.summary }
                : t,
            ),
          );
          if (runCmdIds.current.has(data.tool_call_id)) {
            terminalRef.current?.writeExit(data.exit_code, data.duration_seconds, data.timed_out);
          }
          setBusy(false);
        },
        onError: (data) => {
          terminalRef.current?.write(`\r\n\x1b[31m[error: ${data.message}]\x1b[0m\r\n`);
          setBusy(false);
        },
      },
      controller.signal,
    ).catch(() => {
      setBusy(false);
    });
  };

  // Wake a hibernated project via /resume — no LLM call, no credit cost.
  // Called by the auto-wake grace timer, or immediately when the user shows
  // intent (clicks a file, focuses the chat input, opens the preview) so they
  // never wait out the grace period. Also handles 'starting': a row loaded in
  // that state means a previous visit's provision is still in flight (or
  // quietly finished) — /resume attaches to it and reports ready. No-op when
  // the container is live.
  const wakeProject = () => {
    if (!projectId || wakeStartedRef.current) return;
    if (
      containerState !== 'stopped' &&
      containerState !== 'hibernating' &&
      containerState !== 'starting'
    ) return;
    wakeStartedRef.current = true;
    setWaking(true);
    setWakeFailed(false);
    setContainerState('starting');
    const fail = () => {
      setWaking(false);
      setWakeFailed(true);
      setContainerState('stopped');
    };
    capstoneApi.streamResume(projectId, {
      onContainerStatus: (data) => {
        setContainerState(data.state);
        if (data.state === 'ready') {
          setWaking(false);
          setPreviewNonce((n) => n + 1);   // re-mount the iframe onto the revived app
          // Workspace was just restored from blob — refresh the file tree,
          // which loaded empty while the project was asleep. Best-effort.
          capstoneApi.getProject(projectId).then((p) => {
            setFiles(p.files || []);
            if (p.container?.preview_url) setPreviewUrl(p.container.preview_url);
          }).catch(() => { /* tree refreshes on the next iterate anyway */ });
        }
        if (data.preview_url) {
          setPreviewUrl(data.preview_url);
          setOutputTab('preview');   // auto-jump to the playable build
        }
      },
      onError: fail,
    }).catch(fail);
  };

  // Auto-wake grace timer: project loaded asleep → wait WAKE_GRACE_MS, then
  // resume. Unmount (user peeked and left) cancels the timer, so nothing is
  // provisioned. wakeStartedRef keeps a failed wake from re-arming the timer —
  // the asleep overlay's "Wake project" button is the explicit retry path.
  useEffect(() => {
    if (!project) return;
    if (wakeStartedRef.current) return;
    // Loaded in 'starting': a previous visit already kicked off the provision
    // (billing already started, Azure builds on regardless) — attach NOW; the
    // grace period only exists to avoid TRIGGERING a provision on a stray
    // click. Without this, an interrupted wake left the page on the boot
    // spinner forever with nothing driving it.
    if (containerState === 'starting') {
      wakeProject();
      return;
    }
    if (containerState !== 'stopped' && containerState !== 'hibernating') return;
    const t = setTimeout(wakeProject, WAKE_GRACE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, containerState]);

  // Auto-fire the first message after project state loads (when the user came
  // from CapstonePage's intent prompt). Guard against double-fire on re-renders.
  useEffect(() => {
    if (!project || !autoStartMessage || autoStartFired || busy) return;
    if ((project.iteration_count || 0) > 0) {
      // Project already has iterations — don't auto-replay the problem statement.
      setAutoStartFired(true);
      return;
    }
    setAutoStartFired(true);
    handleSend(autoStartMessage);
    // Clear navigation state so a refresh doesn't re-trigger.
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, autoStartMessage, autoStartFired]);

  // Fetch file content whenever selectedFile changes
  useEffect(() => {
    if (!projectId || !selectedFile) return;
    let cancelled = false;
    setFileLoading(true);
    setFileError(null);
    setFileContent(null);
    capstoneApi.getFileContent(projectId, selectedFile)
      .then((res) => {
        if (cancelled) return;
        setFileContent(res.content);
        setFileLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const msg = String(err?.message || '');
        if (msg.includes('container_asleep')) {
          // Backend says there's no live container — flip the whole page to
          // the honest asleep state instead of raw error JSON in the viewer.
          // Flipping to 'stopped' re-arms the auto-wake timer, so the project
          // starts waking on its own — the copy just asks for patience.
          setContainerState('stopped');
          setFileError(
            'This project is asleep — its files are stored safely. ' +
            'Waking it up now; click the file again in a moment.',
          );
        } else {
          setFileError(msg || 'Failed to load file.');
        }
        setFileLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, selectedFile]);

  // Cycle the reassurance message while the container is provisioning.
  useEffect(() => {
    if (containerState !== 'starting') return;
    const t = setInterval(() => setBootMsgIdx((i) => i + 1), 4000);
    return () => clearInterval(t);
  }, [containerState]);

  // Elapsed-time ticker while an iteration is running.
  useEffect(() => {
    if (!busy) { setBusySeconds(0); return; }
    const t = setInterval(() => setBusySeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);

  if (loadError) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">Can't open this project</h2>
            <p className="text-sm text-muted-foreground mb-4">{loadError}</p>
            <button
              type="button"
              onClick={() => navigate('/capstone')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
            >
              Back to My Capstones
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
          Loading project…
        </div>
      </div>
    );
  }

  const pill = STATUS_PILL[project.status];

  // Download button — reused in the Workspace pane header. Only meaningful once
  // there are files and the container is live.
  const downloadButton =
    files.length > 0 && containerState === 'ready' ? (
      <a
        href={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/capstone/projects/${projectId}/zip`}
        onClick={async (e) => {
          e.preventDefault();
          try {
            const token = localStorage.getItem('access_token');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const resp = await fetch(`${apiUrl}/api/capstone/projects/${projectId}/zip`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
              const err = await resp.text();
              alert(`Download failed: ${resp.status} ${err}`);
              return;
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(project.title || 'capstone').replace(/[^a-z0-9]/gi, '_').slice(0, 64)}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } catch (err: any) {
            alert(`Download error: ${err?.message || err}`);
          }
        }}
        className="shrink-0 text-xs px-2 py-0.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white font-medium"
        title="Download all workspace files as a zip"
      >
        ↓ Download
      </a>
    ) : null;

  // Chat pane — used by both the desktop resizable layout and the mobile
  // single-column view.
  const chatPane = (
    <ChatPane
      initialMessages={messages}
      streamingAssistant={streamingAssistant}
      streamingRecap={streamingRecap}
      toolEvents={toolEvents}
      busy={busy}
      onSend={handleSend}
      onRerun={handleRerun}
      onInputFocus={wakeProject}
    />
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-3 shrink-0">
          {/* Row 1: meta on the left, Download aligned to the right edge */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/capstone')}
                className="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 shrink-0"
                aria-label="Back to project list"
              >
                ← My Capstones
              </button>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${pill.classes}`}>
                {pill.label}
              </span>
              <span className="text-xs text-gray-500 shrink-0">iter {project.iteration_count}</span>
            </div>

            {/* Regular Cubemate menu (consistent with other pages) */}
            <div className="flex items-center gap-2 shrink-0">
              {!isUltimate && (
                <button
                  type="button"
                  onClick={() => navigate('/settings?tab=subscription')}
                  className="hidden md:block px-3 py-1.5 text-xs bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all"
                  title="Upgrade to premium"
                >
                  Upgrade +
                </button>
              )}
              <span className="hidden md:inline-flex">
                <DocumentationButton onClick={() => setDocumentationOpen(true)} />
              </span>
              <ThemeToggle />
            </div>
          </div>

          {/* Title + a light, label-free topic hint (values only — no "Lang"/"Concepts") */}
          <h1 className="text-lg font-semibold text-foreground mt-1 truncate">
            {project.title || project.problem_statement.slice(0, 80) || 'Untitled project'}
          </h1>
          {history && history.concepts.length > 0 && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
              {history.concepts.slice(0, 5).join(' · ')}
            </div>
          )}
        </header>

        {/* Plan checklist as a full-width banner across the 3 panes */}
        <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 shrink-0">
          <PlanChecklist plan={plan} busy={busy} busySeconds={busySeconds} />
        </div>

        {/* 3-pane body — desktop dividers are drag-resizable (each pane min 20%,
            so any pane can grow past 50%); layout persists via localStorage.
            Mobile keeps the single-column chat view. */}
        {!isDesktop ? (
          <div className="flex-1 flex flex-col overflow-hidden">{chatPane}</div>
        ) : (
        <PanelGroup direction="horizontal" autoSaveId="capstone-panes" className="flex-1 min-h-0 overflow-hidden">
          {/* Left: chat (with tool activity inlined) */}
          <Panel defaultSize={33} minSize={20} className="flex flex-col overflow-hidden">
            {chatPane}
          </Panel>
          <PanelResizeHandle
            onDragging={setPaneDragging}
            className="w-1 shrink-0 cursor-col-resize bg-gray-200 dark:bg-gray-800 hover:bg-purple-400 data-[resize-handle-state=drag]:bg-purple-500 transition-colors"
          />

          {/* Pane 2: Workspace — file tree + code viewer (the learning surface) */}
          <Panel defaultSize={34} minSize={20} className="flex flex-col overflow-hidden">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 px-4 py-2 border-b border-gray-200 dark:border-gray-800 shrink-0 flex items-center justify-between gap-2">
              <span>Workspace{files.length > 0 && ` · ${files.length}`}</span>
              {downloadButton}
            </div>
            <div className="h-2/5 min-h-0 overflow-hidden border-b border-gray-200 dark:border-gray-800">
              <FileTree
                files={files}
                containerState={containerState}
                selectedPath={selectedFile}
                onSelect={(path) => {
                  wakeProject();   // intent: skip the grace period if asleep
                  setSelectedFile(path);
                }}
              />
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <CodeViewer
                path={selectedFile}
                content={fileContent}
                loading={fileLoading}
                error={fileError}
              />
            </div>
          </Panel>
          <PanelResizeHandle
            onDragging={setPaneDragging}
            className="w-1 shrink-0 cursor-col-resize bg-gray-200 dark:bg-gray-800 hover:bg-purple-400 data-[resize-handle-state=drag]:bg-purple-500 transition-colors"
          />

          {/* Pane 3: Output — Terminal ⟷ ▶ Play (terminal stays mounted, hidden under preview) */}
          <Panel defaultSize={33} minSize={20} className="flex flex-col overflow-hidden">
            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-200 dark:border-gray-800 shrink-0">
              {/* Segmented tab control (#148 polish): both tabs live in one
                  pill container and the active one is a raised chip — reads
                  as a tab group, not two stray buttons. Container status is
                  the dot on the right, NOT text inside a tab label. */}
              <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => setOutputTab('terminal')}
                  className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                    outputTab === 'terminal'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  Terminal
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!previewUrl) return;
                    wakeProject();   // intent: skip the grace period if asleep
                    setOutputTab('preview');
                  }}
                  disabled={!previewUrl}
                  title={previewUrl
                    ? 'Open the live preview'
                    : 'Available once the build produces something to show (a game or a running app)'}
                  className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                    outputTab === 'preview' && previewUrl
                      // Raised chip with purple identity — "you are here",
                      // quieter than the actionable Open button (#148).
                      ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-300 shadow-sm'
                      : previewUrl
                        // Invite state: on the Terminal tab with a build
                        // ready, Preview earns the purple accent — click me.
                        ? 'text-purple-600 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                        : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  }`}
                >
                  ▶ Preview
                </button>
              </div>
              {/* The real next action while IN preview: full-size the app.
                  The one solid-purple button in the row (#148). */}
              {outputTab === 'preview' && previewUrl && (
                <a
                  href={capstonePreviewSrc(previewUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs px-3 py-1 rounded-md font-medium bg-purple-600 hover:bg-purple-700 text-white"
                  title="Open the running app full-size in a new browser tab"
                >
                  ↗ Open full view
                </a>
              )}
              <div className="ml-auto flex items-center gap-2">
                {/* Status readout, not a control: green ready / pulsing amber
                    starting / gray asleep. Hidden when no container has ever
                    existed ('none'/null). */}
                {containerState && containerState !== 'none' && (
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        containerState === 'ready'
                          ? 'bg-emerald-500'
                          : containerState === 'starting'
                            ? 'bg-amber-400 animate-pulse'
                            : 'bg-gray-400 dark:bg-gray-600'
                      }`}
                    />
                    {containerState === 'stopped' || containerState === 'hibernating'
                      ? 'asleep'
                      : containerState}
                  </span>
                )}
                {outputTab === 'terminal' && lastCommand && (
                  <button
                    type="button"
                    onClick={() => handleRerun(lastCommand)}
                    disabled={busy}
                    title={`Re-run: ${lastCommand}`}
                    className="text-xs font-medium px-2 py-0.5 rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ↻ Re-run
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 relative">
              {/* Reassurance overlay during the (possibly multi-minute) first provision */}
              {containerState === 'starting' && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#0b0e14]/95 text-center px-6">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  <div className="text-sm text-gray-200">
                    {waking
                      ? 'Waking your project — restoring your files and restarting the app…'
                      : BOOT_MESSAGES[bootMsgIdx % BOOT_MESSAGES.length]}
                  </div>
                  <div className="text-xs text-gray-500">
                    {waking
                      ? 'Usually takes under a minute — everything is exactly where you left it.'
                      : 'First run can take a few minutes — you can keep chatting while it sets up.'}
                  </div>
                </div>
              )}
              {/* Hibernated project (v3.22 #117 honest state → v3.26 auto-wake):
                  the waking spinner shows from the first paint — same copy as
                  the 'starting' overlay so the grace-period → real-provision
                  transition is seamless. The actual /resume call fires after
                  WAKE_GRACE_MS (or instantly on user intent). The 🌙 + button
                  only appear after a FAILED wake, as the manual retry path. */}
              {(containerState === 'stopped' || containerState === 'hibernating') && !busy && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#0b0e14]/95 text-center px-6">
                  {wakeFailed ? (
                    <>
                      <div className="text-3xl">🌙</div>
                      <div className="text-sm text-gray-200">This project is asleep</div>
                      <div className="text-xs text-gray-500 max-w-xs">
                        Waking it didn't work this time — your files and data are still safely stored.
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          wakeStartedRef.current = false;
                          wakeProject();
                        }}
                        className="text-xs px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white font-medium"
                      >
                        Wake project
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                      <div className="text-sm text-gray-200">
                        Waking your project — restoring your files and restarting the app…
                      </div>
                      <div className="text-xs text-gray-500 max-w-xs">
                        Usually takes under a minute — everything is exactly where you left it.
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Terminal: always mounted so streamed output + scrollback survive tab switches */}
              <div className={`absolute inset-0 bg-[#0b0e14] p-1 ${outputTab === 'terminal' ? '' : 'hidden'}`}>
                <CapstoneTerminal ref={terminalRef} />
              </div>
              {previewUrl && (
                <div
                  className={`absolute inset-0 bg-black ${outputTab === 'preview' ? '' : 'hidden'} ${
                    paneDragging ? 'pointer-events-none' : ''
                  }`}
                >
                  <iframe
                    key={`${previewUrl}#${previewNonce}`}
                    title="Preview"
                    src={capstonePreviewSrc(previewUrl)}
                    className="w-full h-full border-0"
                    allow="autoplay; fullscreen; gamepad"
                  />
                </div>
              )}
            </div>
          </Panel>
        </PanelGroup>
        )}
      </div>

      <DocumentationModal
        isOpen={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
        currentRoute="capstone"
        onPromptClick={(prompt: string) => {
          setDocumentationOpen(false);
          handleSend(prompt);
        }}
      />
    </div>
  );
};

export default CapstoneProjectPage;
